import React from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Icon,
  alpha,
  useTheme,
} from "@mui/material";

interface COAEmptyViewProps {
  type: "no-branch" | "no-data" | "error";
  onCreateAccount?: () => void;
  onRefresh?: () => void;
  errorMessage?: string;
}

export const COAEmptyView: React.FC<COAEmptyViewProps> = ({
  type,
  onCreateAccount,
  onRefresh,
  errorMessage,
}) => {
  const theme = useTheme();

  const getEmptyStateConfig = () => {
    switch (type) {
      case "no-branch":
        return {
          icon: "business",
          title: "Select a Branch First",
          description:
            "Please select a branch from the header to view its Chart of Accounts. You can choose 'Organization Level' to see all accounts across branches.",
          primaryAction: null,
          secondaryAction: null,
          color: theme.palette.warning.main,
        };

      case "no-data":
        return {
          icon: "account_tree",
          title: "No Accounts Found",
          description:
            "This branch doesn't have any Chart of Accounts set up yet. Create your first account to get started with the accounting structure.",
          primaryAction: {
            label: "Create First Account",
            action: onCreateAccount,
            icon: "add",
          },
          secondaryAction: {
            label: "Refresh",
            action: onRefresh,
            icon: "refresh",
          },
          color: theme.palette.info.main,
        };

      case "error":
        return {
          icon: "error_outline",
          title: "Failed to Load Accounts",
          description:
            errorMessage ||
            "There was an error loading the Chart of Accounts. Please try refreshing the page or contact support if the issue persists.",
          primaryAction: {
            label: "Try Again",
            action: onRefresh,
            icon: "refresh",
          },
          secondaryAction: null,
          color: theme.palette.error.main,
        };

      default:
        return {
          icon: "account_tree",
          title: "No Data",
          description: "No information available.",
          primaryAction: null,
          secondaryAction: null,
          color: theme.palette.text.secondary,
        };
    }
  };

  const config = getEmptyStateConfig();

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "400px",
        p: 3,
      }}
    >
      <Card
        sx={{
          maxWidth: 480,
          width: "100%",
          textAlign: "center",
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.05)}`,
        }}
      >
        <CardContent sx={{ p: 4 }}>
          {/* Icon */}
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 80,
              height: 80,
              borderRadius: "50%",
              bgcolor: alpha(config.color, 0.1),
              mb: 3,
            }}
          >
            <Icon
              sx={{
                fontSize: 40,
                color: config.color,
              }}
            >
              {config.icon}
            </Icon>
          </Box>

          {/* Title */}
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              mb: 2,
              color: "text.primary",
            }}
          >
            {config.title}
          </Typography>

          {/* Description */}
          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              mb: 4,
              lineHeight: 1.6,
              maxWidth: 400,
              mx: "auto",
            }}
          >
            {config.description}
          </Typography>

          {/* Actions */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            {config.primaryAction && (
              <Button
                variant="contained"
                size="large"
                startIcon={
                  config.primaryAction.icon ? (
                    <Icon>{config.primaryAction.icon}</Icon>
                  ) : undefined
                }
                onClick={config.primaryAction.action}
                sx={{
                  px: 3,
                  py: 1.5,
                  fontWeight: 600,
                  textTransform: "none",
                  borderRadius: 2,
                }}
              >
                {config.primaryAction.label}
              </Button>
            )}

            {config.secondaryAction && (
              <Button
                variant="outlined"
                size="large"
                startIcon={
                  config.secondaryAction.icon ? (
                    <Icon>{config.secondaryAction.icon}</Icon>
                  ) : undefined
                }
                onClick={config.secondaryAction.action}
                sx={{
                  px: 3,
                  py: 1.5,
                  fontWeight: 600,
                  textTransform: "none",
                  borderRadius: 2,
                  borderColor: alpha(config.color, 0.3),
                  color: config.color,
                  "&:hover": {
                    borderColor: config.color,
                    bgcolor: alpha(config.color, 0.05),
                  },
                }}
              >
                {config.secondaryAction.label}
              </Button>
            )}
          </Box>

          {/* Additional info for no-branch state */}
          {type === "no-branch" && (
            <Box
              sx={{
                mt: 3,
                p: 2,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.warning.main, 0.05),
                border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  fontSize: "0.875rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                }}
              >
                <Icon sx={{ fontSize: 16, color: "warning.main" }}>info</Icon>
                Use the branch selector in the top-right corner
              </Typography>
            </Box>
          )}

          {/* Additional info for error state */}
          {type === "error" && errorMessage && (
            <Box
              sx={{
                mt: 3,
                p: 2,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.error.main, 0.05),
                border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "error.main",
                  fontSize: "0.75rem",
                  fontFamily: "monospace",
                }}
              >
                {errorMessage}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};
